import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CircularProgress,
  Grid,
  Button,
  MenuItem,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Dashboard as DashboardIcon,
  Email as EmailIcon,
  Subscriptions as SubscriptionsIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Settings as SettingsIcon,
  LocationOn as LocationIcon,
  AdminPanelSettings as UsersIcon,
  CreditCard as PlansIcon,
  Timeline as LifecycleIcon,
  AccountBalance as MercadoPagoIcon,
  Flag as FlagIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { superAdminApi, SubscriptionPlan, TenantDomain } from '../services/superAdminApi';
import TextField from '../components/common/TextField';

function TabPanel({ children, value, index }: { children?: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`superadmin-tabpanel-${index}`} aria-labelledby={`superadmin-tab-${index}`}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const StatCard: React.FC<{ title: string; value: React.ReactNode; helper?: string; color?: string }> = ({ title, value, helper, color }) => (
  <Card>
    <CardHeader title={title} />
    <CardContent>
      <Typography variant="h4" sx={{ color: color || 'text.primary' }}>{value}</Typography>
      {helper && <Typography variant="body2" color="text.secondary">{helper}</Typography>}
    </CardContent>
  </Card>
);

type DashboardStats = {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  suspendedTenants: number;
  newTenantsThisMonth: number;
  expiringTrialsThisWeek: number;
  monthlyRevenue: number;
  annualRevenue: number;
  lastUpdated?: string;
};

type TenantAnalytics = {
  tenantId: number;
  name: string;
  subdomain: string;
  adminEmail?: string;
  status: string;
  subscriptionActive: boolean;
  billingPlan: string;
  billingPlanId?: number;
  monthlyRevenue?: number;
  memberCount?: number;
  lastActivity?: string;
  createdAt?: string;
  trialEndsAt?: string;
  subscriptionEndsAt?: string;
  daysUntilExpiry?: number;
  isExpiringSoon?: boolean;
};

type InvitationDto = {
  id: number;
  email: string;
  role: string;
  status: string;
  expiresAt?: string;
  createdAt: string;
  acceptedAt?: string;
  token: string;
};

type SubscriptionInfo = {
  plan?: string;
  subscriptionPlanId?: number;
  status: string;
  subscriptionActive: boolean;
  subscriptionEndsAt?: string;
  demoEndsAt?: string;
  maxProducts?: number;
  maxChannels?: number;
  maxUsers?: number;
};

type FeatureFlag = { key: string; enabled: boolean };

type PlanFormState = {
  id?: number;
  name: string;
  currency: string;
  price: number;
  isActive: boolean;
  features: string;
};

const DEFAULT_PLAN_OPTIONS = ['Starter', 'Growth', 'Scale', 'Enterprise'];
const STATUS_OPTIONS = ['Active', 'Demo', 'Suspended', 'Cancelled', 'Expired', 'PendingPayment'];
const ROLE_OPTIONS = ['Admin', 'User', 'Seller'];

const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString('es-AR') : '-');
const formatInputDate = (value?: string) => (value ? value.substring(0, 16) : '');

const parseFeatures = (json?: string) => {
  try {
    const parsed = JSON.parse(json || '[]');
    return Array.isArray(parsed) ? parsed.map((item: any) => String(item)) : [];
  } catch {
    return [];
  }
};

export const SuperAdminDashboardPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [analytics, setAnalytics] = useState<TenantAnalytics[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsFilter, setAnalyticsFilter] = useState({ search: '', status: 'all', plan: 'all' });
  const [tenants, setTenants] = useState<any[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<number | ''>('');
  const [globalError, setGlobalError] = useState<string | null>(null);

  const [invitationForm, setInvitationForm] = useState({ email: '', role: 'Admin', expiresAt: '' });
  const [invitationMessage, setInvitationMessage] = useState<string | null>(null);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitations, setInvitations] = useState<InvitationDto[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);

  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [subscriptionForm, setSubscriptionForm] = useState<SubscriptionInfo | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState<string | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  const [domains, setDomains] = useState<TenantDomain[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [domainsLoading, setDomainsLoading] = useState(false);

  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [flagsSaving, setFlagsSaving] = useState(false);
  const [flagsMessage, setFlagsMessage] = useState<string | null>(null);

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planForm, setPlanForm] = useState<PlanFormState>({ name: '', currency: 'USD', price: 0, isActive: true, features: '' });
  const [planSaving, setPlanSaving] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  const [revenueRange, setRevenueRange] = useState({ fromDate: '', toDate: '' });
  const [revenueValue, setRevenueValue] = useState<number | null>(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  const navigate = useNavigate();

  const selectedTenant = useMemo(() => tenants.find((t) => t.id === selectedTenantId), [tenants, selectedTenantId]);
  const planOptions = plans.length ? plans : DEFAULT_PLAN_OPTIONS.map((name, idx) => ({ id: -(idx + 1), name, currency: 'USD', price: 0, isActive: true }));
  const planMenuItems: Array<{ value: string | number; label: string }> = plans.length
    ? planOptions.map((p) => ({ value: p.id, label: p.name }))
    : DEFAULT_PLAN_OPTIONS.map((name) => ({ value: name, label: name }));
  const resolvePlanName = (planId?: number, fallback?: string) => {
    const found = planOptions.find((p) => p.id === planId)?.name;
    return found || fallback || '-';
  };
  const hasPlanCatalog = plans.length > 0;

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const resp = await superAdminApi.getDashboardStats();
      if (resp.success) setStats(resp.data as DashboardStats);
    } catch (e: any) {
      setGlobalError(e?.message || 'Error al cargar estadísticas');
    } finally {
      setStatsLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      const resp = await superAdminApi.getTenants();
      const list = resp.data || [];
      setTenants(list);
      if (!selectedTenantId && list.length > 0) setSelectedTenantId(list[0].id);
    } catch (e: any) {
      setGlobalError(e?.message || 'No se pudieron cargar los tenants');
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const resp = await superAdminApi.getTenantsAnalytics();
      setAnalytics((resp.data as TenantAnalytics[]) || []);
    } catch (e: any) {
      setGlobalError(e?.message || 'No se pudo cargar el detalle de tenants');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadInvitations = async (tenantId: number) => {
    try {
      setInvitationsLoading(true);
      const resp = await superAdminApi.listInvitations(tenantId);
      setInvitations((resp.data as InvitationDto[]) || []);
    } catch (e: any) {
      setInvitationError(e?.message || 'No se pudieron cargar las invitaciones');
    } finally {
      setInvitationsLoading(false);
    }
  };

  const loadSubscription = async (tenantId: number) => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      const resp = await superAdminApi.getSubscription(tenantId);
      const data = (resp.data as SubscriptionInfo) || null;
      const planName = data ? resolvePlanName(data.subscriptionPlanId, data.plan) : undefined;
      setSubscription(data ? { ...data, plan: planName } : null);
      if (data) {
        setSubscriptionForm({
          plan: planName || 'Starter',
          subscriptionPlanId: data.subscriptionPlanId,
          status: data.status || 'Active',
          subscriptionActive: data.subscriptionActive,
          subscriptionEndsAt: data.subscriptionEndsAt || '',
          demoEndsAt: data.demoEndsAt || '',
          maxProducts: data.maxProducts,
          maxChannels: data.maxChannels,
          maxUsers: data.maxUsers,
        });
      }
    } catch (e: any) {
      setSubscriptionError(e?.message || 'No se pudo cargar la suscripción');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const loadDomains = async (tenantId: number) => {
    try {
      setDomainsLoading(true);
      const resp = await superAdminApi.getTenantDomains(tenantId);
      setDomains((resp.data as TenantDomain[]) || []);
    } catch (e: any) {
      setGlobalError(e?.message || 'No se pudieron cargar dominios');
    } finally {
      setDomainsLoading(false);
    }
  };

  const loadFlags = async (tenantId: number) => {
    try {
      setFlagsLoading(true);
      setFlagsMessage(null);
      const resp = await superAdminApi.getFeatureFlags(tenantId);
      setFlags((resp.data as FeatureFlag[]) || []);
    } catch (e: any) {
      setFlagsMessage(e?.message || 'No se pudieron cargar los feature flags');
    } finally {
      setFlagsLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      const resp = await superAdminApi.listPlans();
      setPlans((resp.data as SubscriptionPlan[]) || []);
    } catch (e: any) {
      setGlobalError(e?.message || 'No se pudieron cargar los planes');
    } finally {
      setPlansLoading(false);
    }
  };

  const loadRevenue = async () => {
    try {
      setRevenueLoading(true);
      const resp = await superAdminApi.getRevenue({
        fromDate: revenueRange.fromDate || undefined,
        toDate: revenueRange.toDate || undefined,
      });
      setRevenueValue(typeof resp.data === 'number' ? resp.data : null);
    } catch {
      setRevenueValue(null);
    } finally {
      setRevenueLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
    loadStats();
    loadAnalytics();
    loadPlans();
  }, []);

  useEffect(() => {
    if (selectedTenantId) {
      loadInvitations(Number(selectedTenantId));
      loadSubscription(Number(selectedTenantId));
      loadDomains(Number(selectedTenantId));
      loadFlags(Number(selectedTenantId));
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (subscriptionForm && !subscriptionForm.subscriptionPlanId && plans.length) {
      const match = plans.find((p) => p.name === subscriptionForm.plan);
      if (match) {
        setSubscriptionForm({ ...subscriptionForm, subscriptionPlanId: match.id });
      }
    }
  }, [plans, subscriptionForm]);

  const filteredAnalytics = useMemo(() => {
    const bySearch = analyticsFilter.search.toLowerCase();
    return analytics.filter((a) => {
      const matchesSearch = a.name.toLowerCase().includes(bySearch) || a.subdomain.toLowerCase().includes(bySearch);
      const matchesStatus = analyticsFilter.status === 'all' || a.status === analyticsFilter.status;
      const planValue = analyticsFilter.plan;
      const matchesPlan =
        planValue === 'all' ||
        planValue === String(a.billingPlanId ?? '') ||
        planValue === (a.billingPlan || '');
      return matchesSearch && matchesStatus && matchesPlan;
    });
  }, [analytics, analyticsFilter]);

  const expiringSoon = useMemo(
    () => filteredAnalytics.filter((a) => (a.daysUntilExpiry ?? 99) <= 7 || a.isExpiringSoon),
    [filteredAnalytics]
  );

  const mrrEstimate = useMemo(() => {
    if (!plans.length) return 0;
    const map = new Map<string, number>();
    plans.forEach((p) => map.set((p.name || '').toLowerCase(), Number(p.price) || 0));
    return analytics
      .filter((a) => a.subscriptionActive && (a.status || '').toLowerCase() === 'active')
      .reduce((sum, a) => sum + (map.get((a.billingPlan || '').toLowerCase()) || 0), 0);
  }, [analytics, plans]);

  const planBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    analytics.forEach((a) => {
      const plan = a.billingPlan || 'Sin plan';
      counts.set(plan, (counts.get(plan) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([plan, count]) => ({ plan, count }));
  }, [analytics]);

  const handleCreateInvitation = async () => {
    if (!selectedTenantId) {
      setInvitationError('Selecciona un tenant primero');
      return;
    }
    setInvitationLoading(true);
    setInvitationError(null);
    setInvitationMessage(null);
    try {
      const payload = {
        email: invitationForm.email.trim(),
        role: invitationForm.role,
        expiresAt: invitationForm.expiresAt ? new Date(invitationForm.expiresAt).toISOString() : undefined,
      };
      const resp = await superAdminApi.createInvitation(Number(selectedTenantId), payload);
      if (resp.success) {
        setInvitationMessage(`Invitación creada. Token: ${resp.data?.token ?? ''}`);
        setInvitationForm({ email: '', role: 'Admin', expiresAt: '' });
        loadInvitations(Number(selectedTenantId));
      } else {
        setInvitationError(resp.message || 'No se pudo crear la invitación');
      }
    } catch (e: any) {
      setInvitationError(e?.response?.data?.message || 'Error al crear invitación');
    } finally {
      setInvitationLoading(false);
    }
  };

  const handleSubscriptionSave = async () => {
    if (!selectedTenantId || !subscriptionForm) return;
    setSubscriptionSaving(true);
    setSubscriptionError(null);
    setSubscriptionMessage(null);
    try {
      const payload = {
        subscriptionPlanId: subscriptionForm.subscriptionPlanId,
        plan: subscriptionForm.plan,
        status: subscriptionForm.status,
        subscriptionActive: subscriptionForm.subscriptionActive,
        subscriptionEndsAt: subscriptionForm.subscriptionEndsAt ? new Date(subscriptionForm.subscriptionEndsAt).toISOString() : null,
        demoEndsAt: subscriptionForm.demoEndsAt ? new Date(subscriptionForm.demoEndsAt).toISOString() : null,
        maxProducts: subscriptionForm.maxProducts,
        maxChannels: subscriptionForm.maxChannels,
        maxUsers: subscriptionForm.maxUsers,
      };
      const resp = await superAdminApi.updateSubscription(Number(selectedTenantId), payload);
      if (resp.success) {
        setSubscriptionMessage(resp.message || 'Suscripción actualizada');
        loadSubscription(Number(selectedTenantId));
      } else {
        setSubscriptionError(resp.message || 'No se pudo actualizar la suscripción');
      }
    } catch (e: any) {
      setSubscriptionError(e?.response?.data?.message || 'Error al actualizar la suscripción');
    } finally {
      setSubscriptionSaving(false);
    }
  };

  const handleAddDomain = async () => {
    if (!selectedTenantId || !domainInput.trim()) return;
    try {
      await superAdminApi.createTenantDomain(Number(selectedTenantId), { hostname: domainInput.trim(), isPrimary: domains.length === 0 });
      setDomainInput('');
      loadDomains(Number(selectedTenantId));
    } catch (e: any) {
      setGlobalError(e?.response?.data?.message || 'No se pudo crear el dominio');
    }
  };

  const handleDeleteDomain = async (domainId: number) => {
    if (!selectedTenantId) return;
    const confirmed = window.confirm('¿Eliminar este dominio?');
    if (!confirmed) return;
    await superAdminApi.deleteTenantDomain(Number(selectedTenantId), domainId);
    loadDomains(Number(selectedTenantId));
  };

  const handleSetPrimaryDomain = async (domainId: number) => {
    if (!selectedTenantId) return;
    await superAdminApi.setPrimaryTenantDomain(Number(selectedTenantId), domainId);
    loadDomains(Number(selectedTenantId));
  };

  const handleSaveFlags = async () => {
    if (!selectedTenantId) return;
    setFlagsSaving(true);
    setFlagsMessage(null);
    try {
      const resp = await superAdminApi.updateFeatureFlags(Number(selectedTenantId), flags);
      setFlagsMessage(resp.message || 'Feature flags actualizadas');
    } catch (e: any) {
      setFlagsMessage(e?.response?.data?.message || 'No se pudo guardar');
    } finally {
      setFlagsSaving(false);
    }
  };

  const handlePlanSave = async () => {
    if (!planForm.name.trim()) {
      setPlanError('Ingresa un nombre de plan');
      return;
    }
    setPlanSaving(true);
    setPlanError(null);
    const payload: SubscriptionPlan = {
      id: planForm.id || 0,
      name: planForm.name.trim(),
      currency: planForm.currency || 'USD',
      price: Number(planForm.price) || 0,
      isActive: planForm.isActive,
      featuresJson: planForm.features ? JSON.stringify(planForm.features.split('\n').map((f) => f.trim()).filter(Boolean)) : '[]',
    };
    try {
      const resp = planForm.id
        ? await superAdminApi.updatePlan(planForm.id, payload)
        : await superAdminApi.createPlan(payload);
      if (!resp.success) {
        setPlanError(resp.message || 'No se pudo guardar el plan');
      } else {
        setPlanDialogOpen(false);
        await loadPlans();
      }
    } catch (e: any) {
      setPlanError(e?.response?.data?.message || 'Error al guardar el plan');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleDeletePlan = async (planId: number) => {
    const confirmed = window.confirm('¿Eliminar este plan?');
    if (!confirmed) return;
    await superAdminApi.deletePlan(planId);
    loadPlans();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom>
        Panel de Super Administrador
      </Typography>
      {globalError && <Alert severity="error" sx={{ mb: 2 }}>{globalError}</Alert>}

      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
          <Tab icon={<EmailIcon />} label="Crear Invitación" iconPosition="start" />
          <Tab icon={<PeopleIcon />} label="Gestionar Invitaciones" iconPosition="start" />
          <Tab icon={<SubscriptionsIcon />} label="Suscripciones" iconPosition="start" />
          <Tab icon={<MoneyIcon />} label="Facturación" iconPosition="start" />
          <Tab icon={<LocationIcon />} label="Sedes" iconPosition="start" />
          <Tab icon={<UsersIcon />} label="Usuarios" iconPosition="start" />
          <Tab icon={<PlansIcon />} label="Planes" iconPosition="start" />
          <Tab icon={<FlagIcon />} label="Feature Flags" iconPosition="start" />
          <Tab icon={<LifecycleIcon />} label="Ciclo de Vida" iconPosition="start" />
          <Tab icon={<MercadoPagoIcon />} label="MercadoPago" iconPosition="start" />
          <Tab icon={<SettingsIcon />} label="Mensajería" iconPosition="start" />
          <Tab icon={<SettingsIcon />} label="Configuración" iconPosition="start" />
        </Tabs>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <TextField
          select
          size="small"
          label="Tenant seleccionado"
          value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(Number(e.target.value))}
          sx={{ minWidth: 240 }}
          disabled={tenantsLoading || tenants.length === 0}
        >
          {tenants.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              #{t.id} • {t.name} ({t.subdomain})
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refrescar tenants">
          <span>
            <IconButton onClick={loadTenants} disabled={tenantsLoading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        {selectedTenant && (
          <Chip label={`Plan: ${resolvePlanName(selectedTenant.subscriptionPlanId, selectedTenant.plan)}`} color="primary" variant="outlined" />
        )}
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => { loadStats(); loadAnalytics(); }}>
            Refrescar datos
          </Button>
          {stats?.lastUpdated && <Chip label={`Actualizado: ${formatDateTime(stats.lastUpdated)}`} />}
        </Stack>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6} lg={3}>
            <StatCard title="Total de Tenants" value={statsLoading ? <CircularProgress size={24} /> : (stats?.totalTenants ?? 0)} />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatCard title="Tenants Activos" value={statsLoading ? <CircularProgress size={24} /> : (stats?.activeTenants ?? 0)} color="success.main" helper="Con suscripción al día" />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatCard title="Tenants en Demo" value={statsLoading ? <CircularProgress size={24} /> : (stats?.trialTenants ?? 0)} color="warning.main" helper="Atiende seguimiento" />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatCard title="Expiran esta semana" value={statsLoading ? <CircularProgress size={24} /> : (stats?.expiringTrialsThisWeek ?? 0)} color="error.main" helper="Demo o suscripción" />
          </Grid>
        </Grid>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Buscar"
              size="small"
              placeholder="Nombre o subdominio"
              value={analyticsFilter.search}
              onChange={(e) => setAnalyticsFilter({ ...analyticsFilter, search: e.target.value })}
            />
            <TextField
              label="Estado"
              size="small"
              select
              value={analyticsFilter.status}
              onChange={(e) => setAnalyticsFilter({ ...analyticsFilter, status: e.target.value })}
            >
              <MenuItem value="all">Todos</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Plan"
              size="small"
              select
              value={analyticsFilter.plan}
              onChange={(e) => setAnalyticsFilter({ ...analyticsFilter, plan: String(e.target.value) })}
            >
              <MenuItem value="all">Todos</MenuItem>
              {planMenuItems.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>
            <Chip label={`Riesgo: ${expiringSoon.length}`} color="warning" />
          </Stack>
          <div style={{ height: 420, width: '100%' }}>
            <DataGrid
              rows={filteredAnalytics.map((a) => ({ id: a.tenantId, ...a }))}
              columns={[
                { field: 'tenantId', headerName: 'ID', width: 80 },
                { field: 'name', headerName: 'Nombre', flex: 1 },
                { field: 'subdomain', headerName: 'Subdominio', width: 140 },
                { field: 'billingPlan', headerName: 'Plan', width: 120 },
                {
                  field: 'status',
                  headerName: 'Estado',
                  width: 140,
                  renderCell: (p) => <Chip size="small" label={p.value} color={p.value === 'Active' ? 'success' : p.value === 'Demo' ? 'warning' : 'default'} />,
                },
                {
                  field: 'subscriptionActive',
                  headerName: 'Suscripción',
                  width: 140,
                  renderCell: (p) => <Chip size="small" label={p.value ? 'Activa' : 'Inactiva'} color={p.value ? 'primary' : 'default'} />,
                },
                {
                  field: 'subscriptionEndsAt',
                  headerName: 'Fin Subs.',
                  width: 170,
                  valueFormatter: (p) => (p.value ? formatDateTime(p.value) : '-'),
                },
                {
                  field: 'trialEndsAt',
                  headerName: 'Fin Demo',
                  width: 170,
                  valueFormatter: (p) => (p.value ? formatDateTime(p.value) : '-'),
                },
                { field: 'daysUntilExpiry', headerName: 'Días restantes', width: 140 },
                {
                  field: 'actions',
                  headerName: 'Acciones',
                  width: 140,
                  renderCell: (p) => (
                    <Button size="small" variant="outlined" onClick={() => navigate(`/super-admin/tenants/${p.row.tenantId}`)}>
                      Abrir
                    </Button>
                  ),
                },
              ] as GridColDef[]}
              loading={analyticsLoading}
              rowsPerPageOptions={[10, 25, 50]}
              initialState={{ pagination: { page: 0, pageSize: 10 } }}
            />
          </div>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Crear invitación</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  value={invitationForm.email}
                  onChange={(e) => setInvitationForm({ ...invitationForm, email: e.target.value })}
                  placeholder="admin@cliente.com"
                />
                <TextField
                  select
                  label="Rol"
                  value={invitationForm.role}
                  onChange={(e) => setInvitationForm({ ...invitationForm, role: e.target.value })}
                >
                  {ROLE_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
                <TextField
                  label="Expira"
                  type="datetime-local"
                  value={invitationForm.expiresAt}
                  onChange={(e) => setInvitationForm({ ...invitationForm, expiresAt: e.target.value })}
                  helperText="Opcional. Se enviará en UTC."
                  InputLabelProps={{ shrink: true }}
                />
                {invitationError && <Alert severity="error">{invitationError}</Alert>}
                {invitationMessage && <Alert severity="success">{invitationMessage}</Alert>}
                <Button variant="contained" onClick={handleCreateInvitation} disabled={invitationLoading || !invitationForm.email}>
                  {invitationLoading ? 'Creando...' : 'Crear invitación'}
                </Button>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Alert severity="info">
              Las invitaciones se vinculan al tenant seleccionado. Puedes copiarlas y enviarlas por tu canal preferido.
            </Alert>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Atajos</Typography>
              <ul style={{ marginTop: 8 }}>
                <li>Usa roles Admin para onboarding inicial.</li>
                <li>Configura vencimiento para pruebas controladas.</li>
              </ul>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Invitaciones del tenant</Typography>
          <Button startIcon={<RefreshIcon />} onClick={() => selectedTenantId && loadInvitations(Number(selectedTenantId))} disabled={!selectedTenantId}>
            Refrescar
          </Button>
        </Stack>
        <div style={{ height: 420, width: '100%' }}>
          <DataGrid
            rows={invitations.map((i) => ({ ...i, id: i.id }))}
            loading={invitationsLoading}
            columns={[
              { field: 'email', headerName: 'Email', flex: 1 },
              { field: 'role', headerName: 'Rol', width: 120 },
              { field: 'status', headerName: 'Estado', width: 140 },
              { field: 'expiresAt', headerName: 'Expira', width: 170, valueFormatter: (p) => (p.value ? formatDateTime(p.value) : '-') },
              { field: 'createdAt', headerName: 'Creado', width: 170, valueFormatter: (p) => formatDateTime(p.value) },
              {
                field: 'token',
                headerName: 'Token',
                flex: 1,
                renderCell: (p) => (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={p.value} size="small" />
                    <Tooltip title="Copiar">
                      <IconButton size="small" onClick={() => navigator.clipboard?.writeText(String(p.value))}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ),
              },
            ] as GridColDef[]}
            rowsPerPageOptions={[10, 25]}
          />
        </div>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6">Suscripción</Typography>
                <Tooltip title="Recargar">
                  <span>
                    <IconButton onClick={() => selectedTenantId && loadSubscription(Number(selectedTenantId))} disabled={subscriptionLoading}>
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Alert severity="info" sx={{ mb: 2 }}>
                UI copiada: persiste datos del plan, estado y límites. Los endpoints de cobro/renovación automática aún no están implementados.
              </Alert>
              {subscriptionError && <Alert severity="error" sx={{ mb: 2 }}>{subscriptionError}</Alert>}
              {subscriptionMessage && <Alert severity="success" sx={{ mb: 2 }}>{subscriptionMessage}</Alert>}
              {subscriptionForm ? (
                <Stack spacing={2}>
                  <TextField
                    select
                    label="Plan"
                    value={hasPlanCatalog ? (subscriptionForm.subscriptionPlanId ?? '') : (subscriptionForm.plan || '')}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (hasPlanCatalog) {
                        const nextId = Number(value);
                        setSubscriptionForm({
                          ...subscriptionForm,
                          subscriptionPlanId: nextId,
                          plan: resolvePlanName(nextId, subscriptionForm.plan),
                        });
                      } else {
                        setSubscriptionForm({ ...subscriptionForm, plan: String(value), subscriptionPlanId: undefined });
                      }
                    }}
                  >
                    {planMenuItems.map((p) => (
                      <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Estado"
                    value={subscriptionForm.status}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                  <FormControlLabel
                    control={<Switch checked={subscriptionForm.subscriptionActive} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscriptionActive: e.target.checked })} />}
                    label="Suscripción activa"
                  />
                  <TextField
                    label="Fin suscripción (UTC)"
                    type="datetime-local"
                    value={formatInputDate(subscriptionForm.subscriptionEndsAt)}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscriptionEndsAt: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Fin demo (UTC)"
                    type="datetime-local"
                    value={formatInputDate(subscriptionForm.demoEndsAt)}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, demoEndsAt: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Máx. productos"
                        type="number"
                        value={subscriptionForm.maxProducts ?? 0}
                        onChange={(e) => setSubscriptionForm({ ...subscriptionForm, maxProducts: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Máx. canales"
                        type="number"
                        value={subscriptionForm.maxChannels ?? 0}
                        onChange={(e) => setSubscriptionForm({ ...subscriptionForm, maxChannels: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Máx. usuarios"
                        type="number"
                        value={subscriptionForm.maxUsers ?? 0}
                        onChange={(e) => setSubscriptionForm({ ...subscriptionForm, maxUsers: Number(e.target.value) })}
                      />
                    </Grid>
                  </Grid>
                  <Divider />
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubscriptionSave} disabled={subscriptionSaving}>
                    {subscriptionSaving ? 'Guardando...' : 'Guardar'}
                  </Button>
                </Stack>
              ) : (
                <Typography color="text.secondary">Selecciona un tenant para ver su suscripción.</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Resumen</Typography>
              <Stack spacing={2}>
                <Chip label={`Estado: ${subscription?.status ?? '-'}`} />
                <Chip label={`Plan: ${resolvePlanName(subscription?.subscriptionPlanId, subscription?.plan)}`} color="primary" />
                <Chip label={`Suscripción activa: ${subscription?.subscriptionActive ? 'Sí' : 'No'}`} color={subscription?.subscriptionActive ? 'success' : 'default'} />
                <Chip label={`Demo hasta: ${formatDateTime(subscription?.demoEndsAt)}`} />
                <Chip label={`Fin suscripción: ${formatDateTime(subscription?.subscriptionEndsAt)}`} />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard title="MRR estimado" value={new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'USD' }).format(mrrEstimate)} helper="Calculado por plan activo" />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Planes activos" value={planBreakdown.length} helper="Distribución por plan" />
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Consultar revenue" />
              <CardContent>
                <Stack spacing={2}>
                  <TextField
                    label="Desde"
                    type="date"
                    value={revenueRange.fromDate}
                    onChange={(e) => setRevenueRange({ ...revenueRange, fromDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Hasta"
                    type="date"
                    value={revenueRange.toDate}
                    onChange={(e) => setRevenueRange({ ...revenueRange, toDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button variant="outlined" onClick={loadRevenue} disabled={revenueLoading}>Consultar</Button>
                  {revenueValue !== null && <Typography>Respuesta API: {revenueValue}</Typography>}
                  {revenueValue === null && !revenueLoading && <Alert severity="info">El endpoint aún no expone valores reales.</Alert>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" gutterBottom>Distribución por plan</Typography>
          <div style={{ height: 360, width: '100%' }}>
            <DataGrid
              rows={planBreakdown.map((p, idx) => ({ id: idx + 1, ...p }))}
              columns={[
                { field: 'plan', headerName: 'Plan', flex: 1 },
                { field: 'count', headerName: 'Tenants', width: 140 },
              ]}
              rowsPerPageOptions={[5, 10]}
            />
          </div>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Dominio / hostname"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder="app.cliente.com"
              fullWidth
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddDomain} disabled={!selectedTenantId || domainsLoading}>
              Agregar
            </Button>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          {domainsLoading ? (
            <CircularProgress />
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {domains.map((d) => (
                <Chip
                  key={d.id}
                  label={d.hostname}
                  color={d.isPrimary ? 'primary' : 'default'}
                  icon={d.isPrimary ? <FlagIcon /> : undefined}
                  onDelete={() => handleDeleteDomain(d.id)}
                  deleteIcon={<DeleteIcon />}
                  onClick={() => handleSetPrimaryDomain(d.id)}
                />
              ))}
              {domains.length === 0 && <Typography color="text.secondary">Sin dominios configurados.</Typography>}
            </Stack>
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Contactos admin por tenant</Typography>
          <div style={{ height: 420, width: '100%' }}>
            <DataGrid
              rows={analytics.map((a) => ({ id: a.tenantId, ...a }))}
              loading={analyticsLoading}
              columns={[
                { field: 'tenantId', headerName: 'ID', width: 80 },
                { field: 'name', headerName: 'Tenant', flex: 1 },
                { field: 'adminEmail', headerName: 'Email admin', width: 220 },
                { field: 'status', headerName: 'Estado', width: 140 },
                { field: 'billingPlan', headerName: 'Plan', width: 120 },
                {
                  field: 'actions',
                  headerName: 'Acciones',
                  width: 150,
                  renderCell: (p) => (
                    <Button size="small" onClick={() => navigate(`/super-admin/tenants/${p.row.tenantId}`)}>Editar tenant</Button>
                  ),
                },
              ] as GridColDef[]}
              rowsPerPageOptions={[10, 25]}
            />
          </div>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={7}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Planes de suscripción</Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setPlanForm({ name: '', currency: 'USD', price: 0, isActive: true, features: '' }); setPlanDialogOpen(true); }}>
            Nuevo plan
          </Button>
        </Stack>
        <div style={{ height: 420, width: '100%' }}>
          <DataGrid
            rows={plans.map((p) => ({ ...p, id: p.id }))}
            loading={plansLoading}
            columns={[
              { field: 'name', headerName: 'Nombre', flex: 1 },
              {
                field: 'price',
                headerName: 'Precio',
                width: 120,
                renderCell: (p) => `${p.row.currency} ${p.value}`,
              },
              { field: 'currency', headerName: 'Moneda', width: 120 },
              {
                field: 'isActive',
                headerName: 'Activo',
                width: 120,
                renderCell: (p) => <Chip size="small" label={p.value ? 'Sí' : 'No'} color={p.value ? 'success' : 'default'} />,
              },
              {
                field: 'featuresJson',
                headerName: 'Features',
                flex: 1,
                renderCell: (p) => {
                  const items = parseFeatures(p.value);
                  return items.length ? <Typography variant="body2">{items.join(' • ')}</Typography> : <Typography variant="body2" color="text.secondary">-</Typography>;
                },
              },
              {
                field: 'actions',
                headerName: 'Acciones',
                width: 150,
                renderCell: (p) => (
                  <Stack direction="row" spacing={1}>
                    <IconButton size="small" onClick={() => { setPlanForm({ id: p.row.id, name: p.row.name, currency: p.row.currency, price: p.row.price, isActive: p.row.isActive, features: parseFeatures(p.row.featuresJson).join('\n') }); setPlanDialogOpen(true); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeletePlan(p.row.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                ),
              },
            ] as GridColDef[]}
            rowsPerPageOptions={[10, 25]}
          />
        </div>

        <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{planForm.id ? 'Editar plan' : 'Nuevo plan'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Nombre" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} />
              <TextField
                label="Precio"
                type="number"
                value={planForm.price}
                onChange={(e) => setPlanForm({ ...planForm, price: Number(e.target.value) })}
                InputProps={{ startAdornment: <InputAdornment position="start">{planForm.currency}</InputAdornment> }}
              />
              <TextField
                label="Moneda"
                value={planForm.currency}
                onChange={(e) => setPlanForm({ ...planForm, currency: e.target.value })}
                placeholder="USD"
              />
              <TextField
                label="Features (una por línea)"
                multiline
                minRows={3}
                value={planForm.features}
                onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
              />
              <FormControlLabel
                control={<Switch checked={planForm.isActive} onChange={(e) => setPlanForm({ ...planForm, isActive: e.target.checked })} />}
                label="Plan activo"
              />
              {planError && <Alert severity="error">{planError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlanDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handlePlanSave} variant="contained" disabled={planSaving}>{planSaving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      <TabPanel value={tabValue} index={8}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h6">Feature flags por tenant</Typography>
            <Button startIcon={<RefreshIcon />} onClick={() => selectedTenantId && loadFlags(Number(selectedTenantId))} disabled={flagsLoading}>
              Recargar
            </Button>
          </Stack>
          {flagsMessage && <Alert severity={flagsMessage.includes('No se pudo') ? 'error' : 'success'} sx={{ mb: 2 }}>{flagsMessage}</Alert>}
          {flagsLoading ? (
            <CircularProgress />
          ) : flags.length ? (
            <Grid container spacing={2}>
              {flags.map((f) => (
                <Grid item xs={12} md={6} key={f.key}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography>{f.key}</Typography>
                      <Switch
                        checked={f.enabled}
                        onChange={(e) => setFlags((curr) => curr.map((ff) => (ff.key === f.key ? { ...ff, enabled: e.target.checked } : ff)))}
                      />
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="text.secondary">No hay flags configuradas.</Typography>
          )}
          <Box sx={{ mt: 3 }}>
            <Button variant="contained" onClick={handleSaveFlags} disabled={flagsSaving || !selectedTenantId}>
              {flagsSaving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={9}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Ciclo de vida de tenants</Typography>
          <div style={{ height: 420, width: '100%' }}>
            <DataGrid
              rows={analytics.map((a) => ({ id: a.tenantId, ...a }))}
              loading={analyticsLoading}
              columns={[
                { field: 'name', headerName: 'Tenant', flex: 1 },
                { field: 'status', headerName: 'Estado', width: 140 },
                { field: 'createdAt', headerName: 'Creado', width: 170, valueFormatter: (p) => formatDateTime(p.value) },
                { field: 'trialEndsAt', headerName: 'Fin demo', width: 170, valueFormatter: (p) => formatDateTime(p.value) },
                { field: 'subscriptionEndsAt', headerName: 'Fin suscripción', width: 180, valueFormatter: (p) => formatDateTime(p.value) },
                { field: 'daysUntilExpiry', headerName: 'Días restantes', width: 140 },
              ] as GridColDef[]}
              rowsPerPageOptions={[10, 25]}
            />
          </div>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={10}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>MercadoPago</Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Pendiente: endpoints de conciliación y panel de cobros. Mientras tanto registra las credenciales en backend (`appsettings`) y verifica webhooks manualmente.
          </Alert>
          <Typography variant="body2">
            Usa esta pestaña para documentar merchant IDs y tokens de prueba/producción por tenant. Cuando los endpoints estén listos, conéctalos aquí.
          </Typography>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={11}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Mensajería</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Falta la API para uso de WhatsApp/SMS. De momento deja registradas las necesidades del cliente y activa flags relacionadas.
          </Alert>
          <Stack spacing={1}>
            <Chip label="Registro de plantillas" />
            <Chip label="Límites por plan" />
            <Chip label="Proveedores (Twilio / Evolution)" />
          </Stack>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Configuración general</Typography>
          <Stack spacing={2}>
            <Typography variant="body2">API base: {process.env.REACT_APP_API_URL || '/api'}</Typography>
            <Typography variant="body2">Token de Super Admin almacenado en `localStorage.sa_token`.</Typography>
            <Alert severity="info">No hay más settings globales editables desde UI; usa esta sección como checklist de despliegue.</Alert>
          </Stack>
        </Paper>
      </TabPanel>
    </Container>
  );
};
